import { PlanningDay, type Planning, type PutPlanningBody } from "@eisox/api/core-api";

import type { Place } from "../components/copy-planning-dialog";
import type { CopySchemaType, CopyToDaysSchemaType, CopyToRoomsSchemaType } from "./schema.helper";

export enum TargetDays {
  MondayToSunday = "monday-to-sunday",
  MondayToFriday = "monday-to-friday",
  SaturdayToSunday = "saturday-to-sunday",
  Custom = "custom",
}

export const targetDaysToDays: Record<TargetDays, PlanningDay[]> = {
  [TargetDays.MondayToSunday]: [
    PlanningDay.MONDAY,
    PlanningDay.TUESDAY,
    PlanningDay.WEDNESDAY,
    PlanningDay.THURSDAY,
    PlanningDay.FRIDAY,
    PlanningDay.SATURDAY,
    PlanningDay.SUNDAY,
  ],
  [TargetDays.MondayToFriday]: [
    PlanningDay.MONDAY,
    PlanningDay.TUESDAY,
    PlanningDay.WEDNESDAY,
    PlanningDay.THURSDAY,
    PlanningDay.FRIDAY,
  ],
  [TargetDays.SaturdayToSunday]: [PlanningDay.SATURDAY, PlanningDay.SUNDAY],
  [TargetDays.Custom]: [],
};

interface CopySource {
  /** Ids that the source represents — a CTA group covers several. */
  parentIds: string[];
  /** Raw user-defined plannings from the source, never the merged view. */
  userDefinedPlannings: Planning[];
}

type CopyPlanningBody = Omit<PutPlanningBody, "scope">;

/** Rewrites the source week: copied day, its copies, then untouched days. */
export const copyToDaysToPutPlanningBody = (data: CopyToDaysSchemaType, source: CopySource): CopyPlanningBody => {
  if (source.userDefinedPlannings.some(planning => !planning.userDefined)) {
    throw new Error("Some plannings are not user defined" + JSON.stringify(source.userDefinedPlannings));
  }
  const { dayToCopy, targetDays } = data;
  const copiedPlannings = source.userDefinedPlannings.filter(planning => planning.day === dayToCopy);
  const newPlannings = targetDays
    .filter(day => day !== dayToCopy)
    .flatMap(day => copiedPlannings.map(planning => ({ ...planning, day })));
  const otherPlannings = source.userDefinedPlannings.filter(
    planning => planning.day !== dayToCopy && !targetDays.includes(planning.day),
  );
  return {
    parentIds: source.parentIds,
    plannings: [...copiedPlannings, ...newPlannings, ...otherPlannings],
  };
};

const copyToPlacesToPutPlanningBody = (
  data: CopyToRoomsSchemaType,
  source: CopySource,
  places: Place[],
): CopyPlanningBody => {
  const targetIds = data.targetPlaces.flatMap(
    placeId => places.find(place => place.id === placeId)?.parentIds ?? [placeId],
  );
  return {
    parentIds: targetIds,
    plannings: source.userDefinedPlannings.filter(planning => data.daysToCopy.includes(planning.day)),
  };
};

/** Dispatcher for cards that expose both copy modes (network, CTA). */
export const copyToPutPlanningBody = (data: CopySchemaType, source: CopySource, places: Place[]): CopyPlanningBody => {
  if (data.mode === "by-day") {
    return copyToDaysToPutPlanningBody(data, source);
  }
  return copyToPlacesToPutPlanningBody(data, source, places);
};
